import { ApplicationElement, html } from '@crossiety/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import style from './style.lit.css';

/**
 * This component is used as a navigation quick-link
 *
 * @prop circle - Shape of the icon element, default false - icon will be square
 * @prop href - Link's destination url
 * @prop icon - The name of the icon to draw
 * @prop name - Will be derived from the innerText of the element and will be passed as alt attribute of the image when it is used
 * @prop picture - Can replace icon (optional)
 * @prop target - <a> tag's target property - where the browser should open the link (options: /, _blank, _parent, _self, _top)
 * @prop theme - Color of the icon (options: primary, white)
 *
 * @slot default - For link label
 *
 * @cssprop --size - Component width and height
 * @cssprop --bg - Background color
 * @cssprop --bg-hover - Background hover color
 * @cssprop --bg-active - Background active color
 * @cssprop --icon-size - Icon size
 * @cssprop --icon-color - Icon color
 */

export default class IconLink extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    circle: { type: Boolean },
    href: { type: String },
    icon: { type: String },
    picture: { type: String },
    target: { type: String },
    theme: { type: String },
    name: { state: true },
  };

  /** @internal */
  get classes() {
    return {
      rounded: this.circle,
      primary: this.theme === 'primary',
      white: this.theme === 'white',
    };
  }

  onSloChange() {
    this.name = this.innerText;
  }

  renderPicture() {
    return html`
      <figure>
        <img src="${this.picture}" alt=${ifDefined(this.name)} />
      </figure>
    `;
  }

  renderIcon() {
    return html`<ef-icon name=${this.icon}></ef-icon>`;
  }

  render() {
    return html`<a
      href=${this.href}
      target=${ifDefined(this.target)}
      class=${classMap(this.classes)}>
      ${this.picture ? this.renderPicture() : this.renderIcon()}
      <slot @slotchange=${this.onSloChange}></slot>
    </a>`;
  }
}

customElements.get('xs-icon-link') ||
  customElements.define('xs-icon-link', IconLink);
